import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';

type SEAMarketResponse = paths['/supplier-markets']['get']['responses']['200']['content']['application/json'];

export interface FinanceIQCustomerInfo {
  displayName: string;
  logoUrl: string | null;
}

const fetchSupplierMarketsIncludingInactive = (): Promise<SEAMarketResponse> =>
  apiClient
    // We must set includeInactive to get best fit customer names for historical and inactive data buyers
    .get('api/supplier-experience/supplier-markets', { searchParams: 'includeInactive=true' })
    .json<SEAMarketResponse>();

/**
 * Builds a UUID → { displayName, logoUrl } map for all customers (including inactive markets).
 * Scoped to the FinanceIQ widget — uses non-suspense useQuery so it does not
 * affect loading states elsewhere in the app.
 */
export const useFinanceIQCustomerNames = (): Record<string, FinanceIQCustomerInfo> => {
  const { data } = useQuery({
    queryKey: ['financeiq-supplier-markets-including-inactive'],
    queryFn: fetchSupplierMarketsIncludingInactive,
    staleTime: 1000 * 60 * 15,
  });

  return useMemo(() => {
    const groups = data?.data?.supplierMarketGroups ?? [];
    const seen = new Set<string>();
    const entries: [string, FinanceIQCustomerInfo][] = [];

    for (const group of groups) {
      const uuid = group.meta.details.makerOrganizationUuid;
      if (uuid && !seen.has(uuid)) {
        seen.add(uuid);
        entries.push([
          uuid,
          {
            displayName: group.meta.details.makerDisplayName ?? uuid,
            logoUrl: group.meta.details.makerLogoPngUrl ?? null,
          },
        ]);
      }
    }

    return Object.fromEntries(entries);
  }, [data]);
};
